import React, { useState, useContext } from 'react';
import './PricingModal.css';
import { Check, Info, X, Cloud } from 'lucide-react';
import { AuthContext } from '../contexts/AuthContext';
import { isStripeBillingEnabled } from '../config/stripe';

const PricingModal = ({ isOpen, onClose }) => {
    const [billingCycle, setBillingCycle] = useState('yearly'); // 'monthly' or 'yearly'
    const [selectedPlan, setSelectedPlan] = useState('Pro');
    const { user } = useContext(AuthContext);

    // Stripe Payment Links - Updated with your actual links
    const STRIPE_PAYMENT_LINKS = {
        Pro: 'https://buy.stripe.com/6oUaEX2W8ac8eTw37Ce3e00', // Your Pro payment link
        Enterprise: 'https://buy.stripe.com/bJe8wP54gckg4eS8rWe3e01' // Your Enterprise payment link
    };

    const handleUpgrade = (plan) => {
        if (!isStripeBillingEnabled()) {
            return;
        }
        const paymentLink = STRIPE_PAYMENT_LINKS[plan];
        if (paymentLink && user) {
            // Add user ID as metadata to the URL for tracking
            const urlWithMetadata = `${paymentLink}?client_reference_id=${user.uid}`;
            window.open(urlWithMetadata, '_blank');
        } else if (paymentLink) {
            // Fallback if no user (shouldn't happen in normal flow)
            window.open(paymentLink, '_blank');
        }
    };

    const plans = [
        {
            name: 'Pro',
            subtitle: 'For dedicated learners',
            monthlyPrice: 9.99,
            yearlyPrice: 99,
            features: [
                { text: 'Unlimited AI responses/month', included: true },
                { text: '6/uploads/day', included: true },
                { text: 'Live quizzes', included: true },
                { text: 'Auto-grading', included: true },
                // { text: 'Sell courses', included: false },
                { text: 'Advanced analytics', included: true },
                { text: 'Export features', included: true, icon: <Cloud className="feature-icon" /> },
                { text: 'Email support', included: true, icon: <Info className="feature-icon" /> }
            ],
            popular: true
        },
        // {
        //     name: 'Enterprise',
        //     subtitle: 'For the most ambitious achievers',
        //     monthlyPrice: 25,
        //     yearlyPrice: 250,
        //     features: [
        //         { text: 'Access to the real-time, proactive AI tutor', included: true, icon: <Info className="feature-icon" /> },
        //         { text: 'Unlimited responses/month', included: true },
        //         { text: 'Learning paths', included: true },
        //         { text: 'Advanced analytics', included: true },
        //         { text: 'Priority processing', included: true },
        //     ],
        //     popular: false
        // }
    ];

    if (!isOpen) return null;

    return (
        <div className="pricing-modal-overlay" onClick={onClose}>
            <div className="pricing-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="pricing-modal-close" onClick={onClose}>
                    <X className="close-icon" />
                </button>

                <div className="pricing-modal-header">
                    <h2 className="pricing-modal-title">Unlock unlimited learning with StudySpace</h2>
                </div>

                <div className="pricing-billing-toggle">
                    <button 
                        className={`pricing-toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
                        onClick={() => setBillingCycle('monthly')}
                    >
                        Monthly
                    </button>
                    <button 
                        className={`pricing-toggle-btn ${billingCycle === 'yearly' ? 'active' : ''}`}
                        onClick={() => setBillingCycle('yearly')}
                    >
                        Yearly
                        <span className="pricing-save-badge">17% off</span>
                    </button>
                </div>

                <div className="pricing-modal-cards">
                    {plans.map((plan, index) => {
                        const currentPrice = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
                        const isSelected = selectedPlan === plan.name;
                        
                        return (
                            <div 
                                key={index} 
                                className={`pricing-modal-card ${plan.popular ? 'popular' : ''} ${isSelected ? 'selected' : ''}`}
                                onClick={() => setSelectedPlan(plan.name)}
                            >
                                {plan.popular && (
                                    <div className="pricing-popular-badge">Most Popular</div>
                                )}
                                
                                <div className="pricing-modal-card-header">
                                    <h3 className="pricing-modal-plan-name">{plan.name}</h3>
                                    <p className="pricing-modal-plan-subtitle">{plan.subtitle}</p>
                                </div>

                                <div className="pricing-modal-card-pricing">
                                    <div className="pricing-modal-price">
                                        ${currentPrice.toFixed(2)} /{billingCycle === 'monthly' ? 'month' : 'month'}
                                    </div>
                                </div>

                                <div className="pricing-modal-card-features">
                                    {plan.features.map((feature, featureIndex) => (
                                        <div key={featureIndex} className="pricing-modal-feature-item">
                                            <Check className="pricing-modal-check-icon" />
                                            <span className="pricing-modal-feature-text">
                                                {feature.text}
                                            </span>
                                            {feature.icon && feature.icon}
                                        </div>
                                    ))}
                                </div>

                                <div className="pricing-modal-selection-indicator">
                                    <div className={`selection-circle ${isSelected ? 'selected' : ''}`}>
                                        {isSelected && <Check className="selection-check" />}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div onClick={() => handleUpgrade(selectedPlan)} className="pricing-modal-footer">
                    <button className="pricing-modal-upgrade-btn">
                        Upgrade
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PricingModal;
